import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { Download, TrendingUp, Users, BookOpen, DollarSign, Activity, Calendar, Filter } from 'lucide-react';
import { api } from '../../api'; // Import api
import toast from 'react-hot-toast'; // Import toast

const AdminStats: React.FC = () => {
  const [timeRange, setTimeRange] = useState('month');
  const [selectedMetric, setSelectedMetric] = useState('revenue');
  const [globalStats, setGlobalStats] = useState<any>({
    totalRevenue: 0,
    totalUsers: 0,
    totalCourses: 0,
    activeSessions: 0,
    averageSessionTime: 0,
    conversionRate: 0,
    customerSatisfaction: 0,
    coursesCompleted: 0,
  });
  const [revenueData, setRevenueData] = useState<any[]>([]);
  const [userGrowthData, setUserGrowthData] = useState<any[]>([]);
  const [deviceUsageData, setDeviceUsageData] = useState<any[]>([]);
  const [conversionFunnelData, setConversionFunnelData] = useState<any[]>([]);
  const [courseStatsData, setCourseStatsData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/dashboard');
      const data = response.data;

      setGlobalStats({
        totalRevenue: data.stats.totalRevenue,
        totalUsers: data.stats.totalUsers,
        totalCourses: data.stats.totalCourses,
        activeSessions: 0, // Not available from backend yet
        averageSessionTime: 0, // Not available from backend yet
        conversionRate: 0, // Not available from backend yet
        customerSatisfaction: 0, // Not available from backend yet
        coursesCompleted: 0, // Not available from backend yet
      });

      const monthlyRevenue = data.monthlyStats.map((item: any) => ({
        month: new Date(item._id.year, item._id.month - 1).toLocaleString('fr-FR', { month: 'short' }),
        revenue: item.revenue,
      }));
      setRevenueData(monthlyRevenue);

      setUserGrowthData(data.userGrowth || []);
      setDeviceUsageData(data.deviceUsage || []);
      setCourseStatsData(data.courseStats || []);
      setConversionFunnelData(data.conversionFunnel || []);

    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const exportData = (format: 'pdf' | 'csv') => {
    alert(`Export ${format.toUpperCase()} en cours... (Fonctionnalité à implémenter)`);
  };

  const COLORS = ['#FFB639', '#FFB639', '#FFB639', '#FFB639', '#FFB639', '#FFB639'];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Statistiques & Analytics</h1>
          <div className="flex space-x-3">
            <button
              onClick={() => exportData('pdf')}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              PDF
            </button>
            <button
              onClick={() => exportData('csv')}
              className="flex items-center px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              CSV
            </button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-[#FFB639]/15 rounded-lg">
                <DollarSign className="h-6 w-6 text-[#FFB639]" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Revenus totaux</p>
                <p className="text-2xl font-bold text-gray-900">{globalStats.totalRevenue.toLocaleString()}€</p>
                <p className="text-sm text-[#FFB639]">+23% vs mois dernier</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-[#FFB639]/15 rounded-lg">
                <Users className="h-6 w-6 text-[#FFB639]" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Utilisateurs actifs</p>
                <p className="text-2xl font-bold text-gray-900">{globalStats.totalUsers.toLocaleString()}</p>
                <p className="text-sm text-[#FFB639]">+15% vs mois dernier</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-[#FFB639]/15 rounded-lg">
                <BookOpen className="h-6 w-6 text-[#FFB639]" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Cours terminés</p>
                <p className="text-2xl font-bold text-gray-900">{globalStats.coursesCompleted.toLocaleString()}</p>
                <p className="text-sm text-[#FFB639]">Taux: 78%</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-[#FFB639]/15 rounded-lg">
                <TrendingUp className="h-6 w-6 text-[#FFB639]" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Taux de conversion</p>
                <p className="text-2xl font-bold text-gray-900">{globalStats.conversionRate}%</p>
                <p className="text-sm text-[#FFB639]">Excellent</p>
              </div>
            </div>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Évolution temporelle</h2>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
            >
              <option value="week">7 derniers jours</option>
              <option value="month">30 derniers jours</option>
              <option value="quarter">3 derniers mois</option>
              <option value="year">12 derniers mois</option>
            </select>
          </div>

          {/* Revenue Chart */}
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value, name) => [`${value}€`, 'Revenus']} />
                <Area type="monotone" dataKey="revenue" stroke="#FFB639" fill="#FFB639" fillOpacity={0.3} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* User Growth */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Croissance des utilisateurs</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={userGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="students" stroke="#FFB639" strokeWidth={2} name="Étudiants" />
                  <Line type="monotone" dataKey="professors" stroke="#FFB639" strokeWidth={2} name="Professeurs" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Device Usage */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Utilisation par appareil</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={deviceUsageData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#FFB639"
                    dataKey="value"
                  >
                    {deviceUsageData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Course Performance */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance des cours</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={courseStatsData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="enrollments" fill="#FFB639" name="Inscriptions" />
                <Bar dataKey="completion" fill="#FFB639" name="Taux complétion %" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Conversion Funnel */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Entonnoir de conversion</h3>
          <div className="space-y-4">
            {conversionFunnelData.map((stage, index) => (
              <div key={stage.stage} className="relative">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{stage.stage}</span>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-600">{stage.count.toLocaleString()}</span>
                    <span className="text-sm font-medium text-gray-900">{stage.percentage}%</span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-[#FFB639] to-[#FFB639] h-3 rounded-full transition-all duration-300"
                    style={{ width: `${stage.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Detailed Metrics Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Métriques détaillées</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Métrique
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valeur actuelle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mois dernier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Évolution
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Objectif
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Temps de session moyen
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">45 min</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">42 min</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-[#FFB639] font-medium">+7.1%</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">50 min</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Taux de rétention 30j
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">78%</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">75%</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-[#FFB639] font-medium">+4.0%</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">80%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Satisfaction client
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">4.7/5</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">4.6/5</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-[#FFB639] font-medium">+2.2%</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">4.8/5</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Panier moyen
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">327€</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">312€</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-[#FFB639] font-medium">+4.8%</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">350€</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminStats;